import {
  Box,
  BoxProps,
  Center,
  Flex,
  FlexProps,
  Table,
  TableProps,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { RiArrowDropDownLine, RiArrowDropUpLine } from "@remixicon/react";
import {
  Column,
  ColumnDef,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getGroupedRowModel,
  Row,
  Table as TanstackTable,
  TableMeta,
  TableState,
  useReactTable,
} from "@tanstack/react-table";
import React, { ReactNode, useEffect, useMemo, useRef } from "react";
import { byValues } from "sort-es";
import { useSessionStorage } from "usehooks-ts";

import {
  DataTableToolbar,
  DataTableToolbarFunctionalityProps,
} from "@/common/components/DataTable/DataTableToolbar";
import { DataTableFunctionalityProps } from "@/common/components/DataTable/DataTableTypes";
import ErrorBoundary from "@/common/components/ErrorBoundary";
import { FillScrollWrapper } from "@/common/components/FillScrollWrapper";
import { useOverflow } from "@/common/hooks/useOverflow";
import { TableDisplayMode } from "@/common/hooks/useTableDisplayMode";
import {
  getColumnIds,
  getId,
  getOrderFromStates,
  getVisibilityFromStates,
  stickyHorizontalProps,
  stickyVerticalProps,
} from "@/common/utils/datatable";
import RowExpanderButton from "@/feature/components/RowExpanderButton";
import { VerticalAlignValues } from "@/types/tanstack-table";
import { ColumnSort } from "@reflag/shared/types/columns";
import { ColumnSorter } from "@reflag/shared/utils/sorting";

export const getFetchingFadeProps = (isFetching: boolean = false) =>
  ({
    opacity: isFetching ? 0.6 : 1,
    transition: "opacity 0.075s ease-out",
  }) satisfies BoxProps;

function getColumnSizeStyle<TData>(
  column: Column<TData, unknown>,
  size = column.getSize(),
): React.CSSProperties {
  return {
    maxWidth: `${column.columnDef.maxSize}px`,
    minWidth: `${column.columnDef.minSize}px`,
    width: size === Number.MAX_SAFE_INTEGER ? "auto" : `${size}px`,
  };
}

export type DataTableProps<
  TData extends { subRows?: TData[]; id: string },
  TMeta = unknown,
  TSortBy extends string = string,
  TColumn extends string = string,
> = DataTableFunctionalityProps<TSortBy, TColumn> &
  TableProps & {
    tableId: string;
    data: TData[];
    columns: ColumnDef<TData, any>[];
    defaultColumns?: TColumn[];
    meta?: TableMeta<TMeta>;
    freezeHeader?: boolean;
    freezeFirstColumn?: boolean;
    scrollable?: boolean;
    isFetching?: boolean;
    emptyState?: ReactNode;
    hasPersistedState?: boolean;
    searchPlaceholder?: string;
    toolbarLeftActions?:
      | ((table: TanstackTable<TData>) => ReactNode)
      | ReactNode;
    toolbarRightActions?:
      | ((table: TanstackTable<TData>) => ReactNode)
      | ReactNode;
    columnCustomizerFooter?: ReactNode;
    columnVerticalAlign?: VerticalAlignValues;
    children?: ReactNode;
    _toolbarLeft?: FlexProps;
    _toolbarRight?: FlexProps;
    highlightRow?: (row: Row<TData>) => boolean;
    onRowClick?: (
      row: Row<TData>,
      e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    ) => void;
    displayMode?: TableDisplayMode;
    groupingColumn?: TColumn;
    groupingSorter?: ColumnSorter<TData, TColumn>;
  };

function DataTableInner<
  TData extends { id: string; subRows?: TData[]; [key: string]: any },
  TMeta = unknown,
  TSortBy extends string = string,
  TColumn extends string = string,
>({
  tableId,
  data,
  columns,
  defaultColumns = getColumnIds(columns) as TColumn[],
  meta,
  freezeHeader = true,
  freezeFirstColumn = true,
  scrollable = true,
  isFetching,
  emptyState,
  hasPersistedState = false,
  searchPlaceholder,
  toolbarLeftActions,
  toolbarRightActions,
  columnCustomizerFooter,
  children,
  _toolbarLeft,
  _toolbarRight,
  onRowClick,
  columnVerticalAlign = "middle",
  displayMode = "list",
  // Grouping
  groupingColumn,
  groupingSorter,
  // Search
  canSearch,
  searchQuery,
  onSearchQueryChange,
  // Paginate
  canPaginate,
  pageIndex = 0,
  pageSize = 20,
  totalCount,
  onPageIndexChange,
  // Sort
  canSort,
  sorting,
  onSortingChange,
  // Column States
  canCustomize,
  columnStates,
  onColumnStatesChange,
  highlightRow = () => false,
  ...props
}: DataTableProps<TData, TMeta, TSortBy, TColumn>) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { refXScrollBegin } = useOverflow(scrollRef);

  const frozenColumnId = useMemo(() => {
    return freezeFirstColumn ? getId(columns[0]) : undefined;
  }, [freezeFirstColumn, columns]);

  // Expanded state to local storage
  const displayScope =
    displayMode === "group" ? `${displayMode}:${groupingColumn}` : displayMode;
  const [expanded, setExpanded] = useSessionStorage<ExpandedState>(
    tableId ? `expanded.${displayScope}.${tableId}` : "N/A",
    {},
    {},
  );

  // Determine if we're using grouping
  const isGroupMode = displayMode === "group";

  // Table state
  const tableState: Partial<TableState> = {
    sorting: canSort ? sorting : undefined,
    pagination: canPaginate
      ? {
          pageIndex,
          pageSize,
        }
      : undefined,
    columnVisibility: getVisibilityFromStates(
      columns,
      columnStates || [],
      defaultColumns,
      frozenColumnId,
    ),
    columnOrder: getOrderFromStates(
      columns,
      columnStates || [],
      frozenColumnId,
    ),
    grouping: isGroupMode && groupingColumn ? [groupingColumn] : [],
    expanded: Object.keys(expanded).length > 0 ? expanded : true,
  };

  const table = useReactTable<TData>({
    columns,
    data,
    rowCount: totalCount,
    state: tableState,
    meta,
    manualPagination: true,
    manualSorting: isGroupMode ? false : true,
    manualFiltering: true,
    enableSorting: canSort ?? false,
    enableSortingRemoval: false,
    enableMultiSort: false,
    enableHiding: true,
    enableExpanding: true,
    enableRowSelection: true,
    enableColumnFilters: false,
    enableGrouping: isGroupMode,
    autoResetExpanded: false,
    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
    getGroupedRowModel: (table) => () => {
      // use Tanstack Tables grouping model to get the grouped rows,
      // then sort the rows by the grouping column using the custom groupingSorter
      const result = getGroupedRowModel<TData>()(table)();
      const grouping = table.getState().grouping?.[0] as TColumn | undefined;
      if (grouping && groupingSorter) {
        const sortClauses = groupingSorter(grouping, true);
        result.rows = result.rows
          .map((row) => ({ __raw: row, ...row.original }))
          .sort(byValues(sortClauses))
          .map((row) => row.__raw);
      }
      return result;
    },
    getSubRows:
      displayMode === "tree"
        ? (row) => {
            return "subRows" in row ? (row.subRows as TData[]) : [];
          }
        : undefined,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel<TData>(),
    getExpandedRowModel: getExpandedRowModel<TData>(),
    groupedColumnMode: false,
    onPaginationChange: (updater) => {
      if (typeof updater !== "function") return;
      const pagination = updater(table.getState().pagination);
      onPageIndexChange?.(pagination.pageIndex);
    },
    onSortingChange: (updater) => {
      if (typeof updater !== "function") return;
      const sorting = updater(table.getState().sorting);
      onSortingChange?.(sorting as ColumnSort<TSortBy>[]);
    },
    onExpandedChange: (updater) => {
      if (typeof updater !== "function") return;
      const before = table.getState().expanded;
      const after = updater(before);
      setExpanded(after);
    },
    onStateChange: (updater) => {
      // Individual state changes are handled by the onChange handlers above
      if (typeof updater !== "function") return;
      const newState = updater(table.getState());
      const columnStates = newState.columnOrder.map((key) => ({
        id: key as TColumn,
        shown: newState.columnVisibility[key],
      }));
      onColumnStatesChange?.(columnStates);
    },
  });

  // Set page index to the last page if the data is less the current page index
  useEffect(() => {
    if (
      !isFetching &&
      canPaginate &&
      totalCount !== undefined &&
      totalCount < pageSize * pageIndex
    ) {
      onPageIndexChange?.(Math.floor(totalCount / pageSize));
    }
  }, [
    isFetching,
    totalCount,
    canPaginate,
    onPageIndexChange,
    pageIndex,
    pageSize,
  ]);

  const activeBg = useColorModeValue("gray.50", "gray.750");

  return (
    <ErrorBoundary>
      <DataTableToolbar
        _left={_toolbarLeft}
        _right={_toolbarRight}
        columnCustomizerFooter={columnCustomizerFooter}
        defaultColumns={defaultColumns}
        frozenColumnId={frozenColumnId}
        hasPersistedState={hasPersistedState}
        isLoading={isFetching}
        leftActions={
          typeof toolbarLeftActions === "function"
            ? toolbarLeftActions(table)
            : toolbarLeftActions
        }
        rightActions={
          typeof toolbarRightActions === "function"
            ? toolbarRightActions(table)
            : toolbarRightActions
        }
        searchPlaceholder={searchPlaceholder}
        table={table}
        {...({
          // Search
          canSearch,
          searchQuery,
          onSearchQueryChange,
          // Paginate
          canPaginate,
          totalCount,
          // Column States
          canCustomize,
        } as DataTableToolbarFunctionalityProps<TColumn>)}
      />

      <FillScrollWrapper ref={scrollRef} enabled={scrollable}>
        {data.length ? (
          <Table isolation="isolate" {...props}>
            {children}
            <Thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <Tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <Th
                      key={header.id}
                      cursor={
                        canSort && header.column.getCanSort()
                          ? "pointer"
                          : undefined
                      }
                      py={2}
                      style={getColumnSizeStyle(
                        header.column,
                        header.getSize(),
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                      {...(freezeHeader && stickyVerticalProps)}
                      {...(freezeFirstColumn &&
                        index === 0 && {
                          ...stickyHorizontalProps(
                            !refXScrollBegin ? "appBorder" : "transparent",
                            2,
                          ),
                        })}
                    >
                      <Flex alignItems="center" justifyContent="start">
                        <Box isTruncated>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </Box>
                        <Flex alignItems="center" position="relative">
                          {{
                            asc: (
                              <RiArrowDropUpLine
                                aria-label="sorted ascending"
                                size={24}
                              />
                            ),
                            desc: (
                              <RiArrowDropDownLine
                                aria-label="sorted descending"
                                size={24}
                              />
                            ),
                          }[header.column.getIsSorted() as string] ?? (
                            <Box height="24px" width="24px"></Box>
                          )}
                        </Flex>
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getFetchingFadeProps(isFetching)}>
              {table.getRowModel().rows.map((row) => {
                // When grouped, render a group header row and, if expanded, its child rows beneath
                if (row.getIsGrouped()) {
                  const groupValue = row.getGroupingValue(groupingColumn || "");
                  const visibleColumns = table.getVisibleLeafColumns();
                  const firstVisibleColumn = visibleColumns[0];

                  return (
                    <Tr
                      key={row.id}
                      _dark={{ bg: "gray.850" }}
                      bg="gray.50"
                      data-is-grouped
                      onClick={(e) => {
                        e.preventDefault();
                        row.getToggleExpandedHandler()();
                      }}
                    >
                      <Td
                        colSpan={freezeFirstColumn ? 1 : visibleColumns.length}
                        py={1.5}
                        style={
                          freezeFirstColumn && firstVisibleColumn
                            ? getColumnSizeStyle(firstVisibleColumn)
                            : undefined
                        }
                        {...(freezeFirstColumn &&
                          stickyHorizontalProps(
                            !refXScrollBegin ? "appBorder" : "transparent",
                            1,
                            "inherit",
                          ))}
                      >
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          minW={0}
                          w="full"
                        >
                          <Flex
                            alignItems="center"
                            gap={1}
                            maxW="full"
                            minW={0}
                          >
                            <Box
                              fontSize="sm"
                              fontWeight="medium"
                              minW={0}
                              isTruncated
                            >
                              {(() => {
                                const cell = row
                                  .getAllCells()
                                  .find(
                                    (cell) => cell.column.id === groupingColumn,
                                  );
                                const columnDefCell =
                                  cell?.column.columnDef.cell;
                                const cellContext = cell?.getContext();

                                if (columnDefCell && cellContext) {
                                  return flexRender(columnDefCell, cellContext);
                                }

                                return groupValueRender(groupValue);
                              })()}
                            </Box>
                            <RowExpanderButton
                              count={row.subRows.length}
                              flexShrink={0}
                              isExpanded={row.getIsExpanded()}
                              onToggle={(e) => {
                                e.stopPropagation();
                                row.getToggleExpandedHandler()();
                              }}
                            />
                          </Flex>
                        </Flex>
                      </Td>
                      {freezeFirstColumn && visibleColumns.length > 1 && (
                        <Td colSpan={visibleColumns.length - 1} py={1.5} />
                      )}
                    </Tr>
                  );
                }

                // Handle regular rows
                return (
                  <Tr
                    key={row.id}
                    bg={highlightRow(row) ? activeBg : undefined}
                    id={row.id}
                    onClick={onRowClick ? (e) => onRowClick(row, e) : undefined}
                  >
                    {row.getVisibleCells().map((cell, index) => (
                      <Td
                        key={cell.id}
                        style={getColumnSizeStyle(cell.column)}
                        verticalAlign={
                          cell.column.columnDef.meta?.verticalAlign ??
                          columnVerticalAlign
                        }
                        {...(freezeFirstColumn &&
                          index === 0 && {
                            ...stickyHorizontalProps(
                              !refXScrollBegin ? "appBorder" : "transparent",
                              undefined,
                              highlightRow(row) ? activeBg : undefined,
                            ),
                          })}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        ) : (
          <Center height="full" {...getFetchingFadeProps(isFetching)}>
            {emptyState}
          </Center>
        )}
      </FillScrollWrapper>
    </ErrorBoundary>
  );
}

const groupValueRender = (groupValue: unknown) => {
  if (groupValue === null || groupValue === undefined) {
    return <Text color="dimmed">No value</Text>;
  }
  if (typeof groupValue === "string") {
    return groupValue;
  }
  if (typeof groupValue === "object") {
    if ("name" in groupValue) {
      return String(groupValue.name);
    }
    return JSON.stringify(groupValue);
  }
  return String(groupValue);
};

// Memoize component so it doesn't re-render when props don't change
export const DataTable = React.memo(DataTableInner) as typeof DataTableInner;
